import { Outlet, Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface PersonMe {
  id: string
}

export function Layout() {
  const { user, firebaseUser, logout } = useAuth()
  const location = useLocation()

  const isAdmin = user?.role === 'admin' || user?.role === 'partner'

  // Check if user has a linked Person record and if they have reports
  const { data: myPerson } = useQuery({
    queryKey: ['people', 'me'],
    queryFn: () => api.get<PersonMe>('/people/me').catch(() => null),
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const hasPersonRecord = !!myPerson

  const navItems = [
    { path: '/', label: 'Dashboard' },
    { path: '/projects', label: 'Projects' },
    ...(hasPersonRecord ? [{ path: '/my-work', label: 'My Work' }] : []),
    { path: '/files', label: 'Files' },
    { path: '/apps', label: 'Apps' },
    ...(isAdmin ? [{ path: '/people', label: 'People' }] : []),
    ...(isAdmin ? [{ path: '/admin', label: 'Admin' }] : []),
  ]

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--color-bg)]/90 backdrop-blur-sm border-b border-[var(--color-border)]">
        <div className="px-4 sm:px-8 md:px-16 lg:px-24 py-4 flex justify-between items-center">
          <div className="flex items-center gap-8">
            <Link
              to="/"
              className="no-underline text-[var(--color-text)] font-medium"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              Portal
            </Link>
            <div className="hidden sm:flex gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`no-underline text-sm transition-colors ${
                    isActive(item.path)
                      ? 'text-[var(--color-text)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm text-[var(--color-text-secondary)]">
              {firebaseUser?.email}
            </span>
            {user?.role && (
              <span className="text-xs px-2 py-1 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded">
                {user.role}
              </span>
            )}
            <button
              onClick={() => logout()}
              className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="sm:hidden px-4 pb-3 flex gap-4 overflow-x-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`no-underline text-sm whitespace-nowrap transition-colors ${
                isActive(item.path)
                  ? 'text-[var(--color-text)]'
                  : 'text-[var(--color-text-secondary)]'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <main className="pt-20 sm:pt-16 px-4 sm:px-8 md:px-16 lg:px-24 py-8">
        <Outlet />
      </main>
    </div>
  )
}
